/**
 * Phase 135E — Recording Health Badge for StreamX SaaS Admin Overview
 *
 * Fetches GET /api/admin/livex/recording-health?days=7 and renders a single
 * chip with launch-success-rate, end-to-end replay-ready-rate, and a hover
 * tooltip detailing the volume breakdown. Mirrors the WhatsApp telemetry
 * pattern: turn a raw collection into a one-glance trust signal.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Video, Loader2 } from 'lucide-react';

function pillColor(rate) {
  if (rate >= 95) return { bg: '#052e16', border: '#22c55e', text: '#86efac' };
  if (rate >= 70) return { bg: '#3a2308', border: '#f59e0b', text: '#fcd34d' };
  return { bg: '#3a0a0a', border: '#ef4444', text: '#fca5a5' };
}

export default function RecordingHealthBadge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get('/admin/livex/recording-health?days=7')
      .then((r) => active && setData(r.data))
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#07111F] border-2 border-[#22314F] rounded-xl text-[11px] font-bold text-[#94A3B8]" data-testid="livex-recording-health-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Recording health…
      </div>
    );
  }

  if (!data) return null;

  const total = data.total_streams || 0;
  const launchRate = data.launch_success_rate || 0;
  const replays = data.replays || {};
  const e2eRate = data.end_to_end_rate || 0;
  const deliverable = data.deliverable || 0;
  // Primary metric is end-to-end when there's enough signal, else launch.
  // If literally zero deliverable + zero replays, show "idle" instead.
  const hasSignal = deliverable > 0 || (replays.total || 0) > 0;
  const primary = (replays.total || 0) > 0 ? e2eRate : launchRate;
  const color = hasSignal ? pillColor(primary) : { bg: '#1e293b', border: '#475569', text: '#cbd5e1' };

  const tooltip = hasSignal
    ? `Recording Health · ${primary}% (${replays.ready || 0}/${replays.total || 0} replays ready · ${data.by_status?.started || 0}/${deliverable} egress launches succeeded · last 7 days)`
    : `Recording Health · idle (${total} stream${total === 1 ? '' : 's'} created · no egress attempts yet)`;

  return (
    <div
      className="inline-flex items-center gap-2.5 px-3 py-2 rounded-xl border-2 transition-colors"
      style={{ background: color.bg, borderColor: color.border }}
      title={tooltip}
      data-testid="livex-recording-health-badge"
    >
      <Video className="h-3.5 w-3.5" style={{ color: color.border }} />
      <div className="flex flex-col leading-tight">
        <div className="text-[9px] font-black uppercase tracking-[0.15em]" style={{ color: color.text }}>
          Recording Health
        </div>
        <div className="text-[14px] font-black tabular-nums" style={{ color: color.text }} data-testid="livex-recording-health-value">
          {hasSignal ? `${primary}%` : 'idle'}
        </div>
      </div>
      {hasSignal && (
        <div className="text-[10px] font-bold pl-2 border-l-2 hidden sm:block" style={{ color: color.text, borderColor: color.border + '40' }}>
          <div>{replays.ready || 0}/{replays.total || 0} ready</div>
          <div className="text-[9px] opacity-70">{data.window_days || 7}d window</div>
        </div>
      )}
    </div>
  );
}
